import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { CashSession, FinancialAccount, FinancialAccountType } from './entities/treasury.model';
import { TreasuryService } from './infra/treasury.service';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { tapResponse } from '@ngrx/operators';

interface TreasuryAccountStoreState {
  currentAccount: FinancialAccount | null;
  savedAccountId: string | null;
  deletedAccountId: string | null;
  financialAccounts: FinancialAccount[];
  openCashSessions: CashSession[];
}

const initialTreasuryAccountStoreState: TreasuryAccountStoreState = {
  currentAccount: null,
  savedAccountId: null,
  deletedAccountId: null,
  financialAccounts: [],
  openCashSessions: [],
};

const CREATE_ACCOUNT_CONFIRMATION_ERROR = 'A API não retornou os dados da conta criada. Atualize a listagem antes de continuar.';

const normalizeFinancialAccountPayload = (
  payload: Partial<FinancialAccount>,
  options?: { includeCurrentBalance?: boolean },
): Partial<FinancialAccount> => {
  const normalizedPayload: Partial<FinancialAccount> = {
    FinancialAccountId: payload.FinancialAccountId,
    Name: payload.Name?.trim() ?? '',
    Type: payload.Type,
    InitialBalance: payload.InitialBalance ?? 0,
    AllowNegative: Boolean(payload.AllowNegative),
    Active: payload.Active ?? true,
  };

  if (options?.includeCurrentBalance) {
    normalizedPayload.CurrentBalance = payload.CurrentBalance ?? payload.InitialBalance ?? 0;
  }

  return normalizedPayload;
};

export const TreasuryAccountStore = signalStore(
  { providedIn: 'root' },
  withState<TreasuryAccountStoreState>(initialTreasuryAccountStoreState),
  withReqState(
    'loadAccount',
    'createAccount',
    'updateAccount',
    'deleteAccount',
    'loadFinancialAccounts',
    'loadOpenCashSessions',
  ),
  withReqState(
    'loadAccount',
    'createAccount',
    'updateAccount',
    'deleteAccount',
    'loadFinancialAccounts',
    'loadOpenCashSessions',
  ),
  withComputed((store) => {
    const bankAccounts = computed(() =>
      store.financialAccounts().filter((account) => account.Type === FinancialAccountType.Bank),
    );



    return {
      bankAccounts,
      activeBankAccounts: computed(() =>
        bankAccounts().filter((account) => account.Active),
      ),
    };
  }),
  withMethods((store, treasuryService = inject(TreasuryService)) => {
    const loadAccount = rxMethod<string>(pipe(
      tap(() => patchState(store, {
        currentAccount: null,
        savedAccountId: null,
        deletedAccountId: null,
      }, setPropLoading('loadAccount'))),
      switchMap((financialAccountId) => treasuryService.getFinancialAccount(financialAccountId).pipe(
        tapResponse({
          next: (currentAccount) => patchState(store, {
            currentAccount,
          }, setPropLoaded('loadAccount')),
          error: (error) => patchState(store, {
            currentAccount: null,
          }, setPropError('loadAccount', error))
        }),
      )),
    ));

    const createAccount = rxMethod<Partial<FinancialAccount>>(pipe(
      tap(() => patchState(store, {
        savedAccountId: null,
        deletedAccountId: null,
      }, setPropLoading('createAccount'))),
      switchMap((payload) => treasuryService.createFinancialAccount(normalizeFinancialAccountPayload(payload, { includeCurrentBalance: true })).pipe(
        tapResponse({
          next: (currentAccount) => {
            if (!currentAccount?.FinancialAccountId) {
              patchState(store, {
                currentAccount: null,
                savedAccountId: null,
              }, setPropError('createAccount', new Error(CREATE_ACCOUNT_CONFIRMATION_ERROR)));
              return;
            }

            patchState(store, {
              currentAccount,
              savedAccountId: currentAccount.FinancialAccountId,
            }, setPropLoaded('createAccount'));
          },
          error: (error) => patchState(store, setPropError('createAccount', error))
        }),
      )),
    ));

    const updateAccount = rxMethod<Partial<FinancialAccount>>(pipe(
      tap(() => patchState(store, {
        savedAccountId: null,
      }, setPropLoading('updateAccount'))),
      switchMap((payload) => treasuryService.updateFinancialAccount(normalizeFinancialAccountPayload(payload)).pipe(
        tapResponse({
          next: (currentAccount) => {
            const savedAccountId = currentAccount?.FinancialAccountId ?? payload.FinancialAccountId ?? null;
            const fallbackAccount = !currentAccount && store.currentAccount() && savedAccountId
              ? {
                ...store.currentAccount(),
                ...normalizeFinancialAccountPayload(payload),
                FinancialAccountId: savedAccountId,
              } as FinancialAccount
              : null;

            patchState(store, {
              currentAccount: currentAccount ?? fallbackAccount,
              savedAccountId,
            }, setPropLoaded('updateAccount'));
          },
          error: (error) =>
            patchState(store, setPropError('updateAccount', error))
        }),
      )),
    ));

    const deleteAccount = rxMethod<string>(pipe(
      tap(() => patchState(store, {
        deletedAccountId: null,
      }, setPropLoading('deleteAccount'))),
      switchMap((financialAccountId) => treasuryService.deleteFinancialAccount(financialAccountId).pipe(
        tapResponse({
          next: () => patchState(store, {
            currentAccount: null,
            deletedAccountId: financialAccountId,
          }, setPropLoaded('deleteAccount')),
          error: (error) =>
            patchState(store, setPropError('deleteAccount', error))
        }),
      )),
    ));

    const reset = (): void => {
      patchState(
        store,
        initialTreasuryAccountStoreState,
        setPropInit(
          'loadAccount',
          'createAccount',
          'updateAccount',
          'deleteAccount',
          'loadFinancialAccounts',
          'loadOpenCashSessions',
        ),
      );
    };

    const clearMutationState = (): void => {
      patchState(store, {
        savedAccountId: null,
        deletedAccountId: null,
      },
      setPropInit(
        'createAccount',
        'updateAccount',
        'deleteAccount',
      ));
    };

    const loadFinancialAccounts = rxMethod<void>(pipe(
      filter(() => !store.loadFinancialAccountsLoaded() && !store.loadFinancialAccountsLoading()),
      tap(() => patchState(store, setPropLoading('loadFinancialAccounts'))),
      switchMap(() => treasuryService.getFinancialAccounts().pipe(
        tapResponse({
          next: (financialAccounts) => patchState(store, {
            financialAccounts,
          }, setPropLoaded('loadFinancialAccounts')),
          error: (error) =>
            patchState(store, setPropError('loadFinancialAccounts', error))
        }),
      )),
    ));

    const bankAccountById = (id: string): FinancialAccount | undefined =>
      store.financialAccounts().find((account) => account.FinancialAccountId === id);

    const financialAccountsByTypes = (types: FinancialAccountType[]): FinancialAccount[] =>
      store.financialAccounts().filter((account) => types.includes(account.Type));

    const activeFinancialAccountsByTypes = (types: FinancialAccountType[]): FinancialAccount[] =>
      store.financialAccounts().filter((account) => account.Active && types.includes(account.Type));

    const loadOpenCashSessions = rxMethod<void>(pipe(
      filter(() => !store.loadOpenCashSessionsLoaded() && !store.loadOpenCashSessionsLoading()),
      tap(() => patchState(store, setPropLoading('loadOpenCashSessions'))),
      switchMap(() => treasuryService.getOpenCashSessions().pipe(
        tapResponse({
          next: (sessions) => patchState(store, {
            openCashSessions: sessions,
          }, setPropLoaded('loadOpenCashSessions')),
          error: (error) =>
            patchState(store, setPropError('loadOpenCashSessions', error))
        }),
      )),
    ));

    const openCashSessionByAccountId = (accountId: string): CashSession | undefined =>
      store.openCashSessions().find((session) => session.FinancialAccountId === accountId);

    return {
      loadAccount,
      createAccount,
      updateAccount,
      deleteAccount,
      clearMutationState,
      reset,
      loadFinancialAccounts,
      bankAccountById,
      financialAccountsByTypes,
      activeFinancialAccountsByTypes,
      loadOpenCashSessions,
      openCashSessionByAccountId,
    };
  }),
);
